import React from 'react';

import { createBridgeFocusLayerRender } from '../../../shared/utils/create-bridge-focus-layer-render';
import { createBridgeLayerRender } from '../../../shared/utils/create-bridge-layer-render';
import { DiscardChangesView } from '../../../shared/components/discard-changes/DiscardChangesView';
import { DiscardLayerData, useDiscardLayer } from '../../vehicle-management/layer-manager/hooks/useDiscardLayer';
import { EditEmailAddressView } from '../components/edit-email-address/EditEmailAddressView';
import { EditLayerBridgeData, useAccountSettingsEditLayer } from './hooks/useAccountSettingsEditLayer';
import { EmailLayerData, useEmailLayer } from './hooks/useEmailLayer';
import { applyTextTransformations } from '../../../shared/utils/applyTextTransformations';

const discardLayerRender = createBridgeFocusLayerRender<DiscardLayerData>(
  (data) => (
    <DiscardChangesView
      translationHandler={data.translationHandler}
      onDiscard={data.onDiscard}
      onKeepEditing={data.onKeepEditing}
    />
  ),
);

const editLayerRender = createBridgeLayerRender<EditLayerBridgeData>(
  (data) => typeof data.title === 'string' ? applyTextTransformations(data.title, data.noTranslateTerms) : data.title,
  (data) => data.content,
  (data) => data.onCloseAttempt,
  (data) => data.onGoBack,
);

const emailLayerRender = createBridgeFocusLayerRender<EmailLayerData>(
  (data) => (
    <EditEmailAddressView
      translationHandler={data.translationHandler}
      trackingManager={data.trackingManager}
      logout={data.logout}
      onClose={data.onClose}
    />
  ),
);

interface AccountSettingsLayersProps {
  editActive: boolean;
  editTitle: React.ReactNode;
  editNoTranslateTerms: string[];
  editContent: React.ReactNode;
  editIsDeactivating: boolean;
  editChanges: boolean;
  editGoBack: (() => void) | undefined;
  onEditClose: () => void;
  emailActive: boolean;
  onEmailClose: () => void;
}

export const useAccountSettingsLayers = ({
  editActive,
  editTitle,
  editNoTranslateTerms,
  editContent,
  editIsDeactivating,
  editChanges,
  editGoBack,
  onEditClose,
  emailActive,
  onEmailClose,
}: AccountSettingsLayersProps) => {
  const { openDiscardLayer } = useDiscardLayer(discardLayerRender);

  useAccountSettingsEditLayer(
    editActive,
    onEditClose,
    openDiscardLayer,
    editLayerRender,
    {
      title: editTitle,
      noTranslateTerms: editNoTranslateTerms,
      content: editContent,
      isDeactivating: editIsDeactivating,
      changes: editChanges,
      onGoBack: editGoBack,
    },
  );

  useEmailLayer(emailActive, onEmailClose, emailLayerRender);
};
